/**
 * WGS84 latitude/longitude ↔ UTM, for the encounter's MGRS readout (PRD-0032 SC-018). No
 * library: the transverse Mercator uses Krüger's series in the sixth order of the third
 * flattening (Karney 2011, "Transverse Mercator with an accuracy of a few nanometers"), which
 * stays exact to well under a millimetre across the widened Norway and Svalbard zones.
 * Only the UTM latitudes (80° S to 84° N) are covered; the polar UPS grid is not.
 */

const A_AXIS = 6_378_137
const FLATTENING = 1 / 298.257223563
const K0 = 0.9996
const FALSE_EASTING = 500_000
const FALSE_NORTHING_SOUTH = 10_000_000

/** Third flattening and its powers. */
const N1 = FLATTENING / (2 - FLATTENING)
const N2 = N1 ** 2
const N3 = N1 ** 3
const N4 = N1 ** 4
const N5 = N1 ** 5
const N6 = N1 ** 6
/** First eccentricity. */
const E = Math.sqrt(FLATTENING * (2 - FLATTENING))
/** 2π·A is the meridian's circumference. */
const RECTIFYING_RADIUS = (A_AXIS / (1 + N1)) * (1 + N2 / 4 + N4 / 64 + N6 / 256)

const ALPHA = [
  N1 / 2 - (2 * N2) / 3 + (5 * N3) / 16 + (41 * N4) / 180 - (127 * N5) / 288 + (7891 * N6) / 37800,
  (13 * N2) / 48 - (3 * N3) / 5 + (557 * N4) / 1440 + (281 * N5) / 630 - (1983433 * N6) / 1935360,
  (61 * N3) / 240 - (103 * N4) / 140 + (15061 * N5) / 26880 + (167603 * N6) / 181440,
  (49561 * N4) / 161280 - (179 * N5) / 168 + (6601661 * N6) / 7257600,
  (34729 * N5) / 80640 - (3418889 * N6) / 1995840,
  (212378941 * N6) / 319334400,
]

const BETA = [
  N1 / 2 - (2 * N2) / 3 + (37 * N3) / 96 - N4 / 360 - (81 * N5) / 512 + (96199 * N6) / 604800,
  N2 / 48 + N3 / 15 - (437 * N4) / 1440 + (46 * N5) / 105 - (1118711 * N6) / 3870720,
  (17 * N3) / 480 - (37 * N4) / 840 - (209 * N5) / 4480 + (5569 * N6) / 90720,
  (4397 * N4) / 161280 - (11 * N5) / 504 - (830251 * N6) / 7257600,
  (4583 * N5) / 161280 - (108847 * N6) / 3991680,
  (20648693 * N6) / 638668800,
]

/** Latitude bands, 8° each from 80° S; X is 12° (72° to 84° N). */
export const LATITUDE_BANDS = 'CDEFGHJKLMNPQRSTUVWXX'

export const MIN_UTM_LATITUDE = -80
export const MAX_UTM_LATITUDE = 84

export type Hemisphere = 'N' | 'S'

export interface UtmCoordinate {
  zone: number
  band: string
  hemisphere: Hemisphere
  easting: number
  northing: number
}

const toRadians = (degrees: number) => (degrees * Math.PI) / 180
const toDegrees = (radians: number) => (radians * 180) / Math.PI

/** Longitude folded into [-180, 180). */
export function normaliseLongitude(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180
}

/** The 8° band letter of a latitude inside the UTM range. */
export function latitudeBand(lat: number): string {
  const index = Math.min(Math.floor(lat / 8 + 10), LATITUDE_BANDS.length - 1)
  return LATITUDE_BANDS[Math.max(index, 0)]
}

/** Longitude of a zone's central meridian, in degrees. */
export const centralMeridian = (zone: number) => (zone - 1) * 6 - 180 + 3

/**
 * Grid zones that are not 6° wide, as the meridians they lie between: south-west Norway (band V)
 * widens 32 at the cost of 31, and Svalbard (band X) has 31, 33, 35 and 37 only.
 */
const ZONE_EXCEPTIONS: Readonly<Record<string, readonly [number, number] | null>> = {
  '31V': [0, 3],
  '32V': [3, 12],
  '31X': [0, 9],
  '32X': null,
  '33X': [9, 21],
  '34X': null,
  '35X': [21, 33],
  '36X': null,
  '37X': [33, 42],
}

/** The meridians a grid zone lies between in a band, west then east; `null` where the grid has none. */
export function zoneMeridians(zone: number, band: string): readonly [number, number] | null {
  const key = `${zone}${band}`
  if (key in ZONE_EXCEPTIONS) return ZONE_EXCEPTIONS[key]
  const meridian = centralMeridian(zone)
  return [meridian - 3, meridian + 3]
}

/** The UTM zone of a position, with the Norway and Svalbard exceptions. */
export function utmZone(lat: number, lon: number): number {
  const folded = normaliseLongitude(lon)
  const regular = Math.min(Math.floor((folded + 180) / 6) + 1, 60)
  const band = latitudeBand(lat)
  const holds = (zone: number) => {
    const meridians = zoneMeridians(zone, band)
    return meridians !== null && folded >= meridians[0] && folded < meridians[1]
  }
  // Where a zone is widened or missing, the position lies in a neighbour of its regular zone.
  return [regular, regular - 1, regular + 1].find(holds) ?? regular
}

/**
 * Transverse Mercator of a position in a given zone; `null` outside the UTM latitudes.
 * A zone other than the position's own is allowed (the MGRS parser needs it).
 */
export function projectToZone(lat: number, lon: number, zone: number): UtmCoordinate | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  if (lat < MIN_UTM_LATITUDE || lat > MAX_UTM_LATITUDE) return null
  const phi = toRadians(lat)
  const lambda = toRadians(normaliseLongitude(lon - centralMeridian(zone)))
  // Conformal latitude as its tangent.
  const tau = Math.tan(phi)
  const sigma = Math.sinh(E * Math.atanh((E * tau) / Math.sqrt(1 + tau * tau)))
  const tauPrime = tau * Math.sqrt(1 + sigma * sigma) - sigma * Math.sqrt(1 + tau * tau)
  const xiPrime = Math.atan2(tauPrime, Math.cos(lambda))
  const etaPrime = Math.asinh(Math.sin(lambda) / Math.sqrt(tauPrime ** 2 + Math.cos(lambda) ** 2))
  let xi = xiPrime
  let eta = etaPrime
  ALPHA.forEach((alpha, i) => {
    const j = 2 * (i + 1)
    xi += alpha * Math.sin(j * xiPrime) * Math.cosh(j * etaPrime)
    eta += alpha * Math.cos(j * xiPrime) * Math.sinh(j * etaPrime)
  })
  const hemisphere: Hemisphere = lat < 0 ? 'S' : 'N'
  return {
    zone,
    band: latitudeBand(lat),
    hemisphere,
    easting: FALSE_EASTING + K0 * RECTIFYING_RADIUS * eta,
    northing: (hemisphere === 'S' ? FALSE_NORTHING_SOUTH : 0) + K0 * RECTIFYING_RADIUS * xi,
  }
}

/** Transverse Mercator of a position in its own zone; `null` outside the UTM latitudes. */
export function toUtm(lat: number, lon: number): UtmCoordinate | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  return projectToZone(lat, lon, utmZone(lat, lon))
}

/** Solve the conformal latitude's tangent back to the geodetic one (Karney, eq. 19–21). */
function geodeticTangent(tauPrime: number): number {
  let tau = tauPrime
  for (let i = 0; i < 12; i++) {
    const sigma = Math.sinh(E * Math.atanh((E * tau) / Math.sqrt(1 + tau * tau)))
    const guess = tau * Math.sqrt(1 + sigma * sigma) - sigma * Math.sqrt(1 + tau * tau)
    const step =
      ((tauPrime - guess) / Math.sqrt(1 + guess * guess)) *
      ((1 + (1 - E * E) * tau * tau) / ((1 - E * E) * Math.sqrt(1 + tau * tau)))
    tau += step
    if (Math.abs(step) < 1e-12) break
  }
  return tau
}

/** A UTM coordinate back to latitude and longitude in degrees. */
export function fromUtm(
  zone: number,
  hemisphere: Hemisphere,
  easting: number,
  northing: number
): { lat: number; lon: number } {
  const falseNorthing = hemisphere === 'S' ? FALSE_NORTHING_SOUTH : 0
  const xi = (northing - falseNorthing) / (K0 * RECTIFYING_RADIUS)
  const eta = (easting - FALSE_EASTING) / (K0 * RECTIFYING_RADIUS)
  let xiPrime = xi
  let etaPrime = eta
  BETA.forEach((beta, i) => {
    const j = 2 * (i + 1)
    xiPrime -= beta * Math.sin(j * xi) * Math.cosh(j * eta)
    etaPrime -= beta * Math.cos(j * xi) * Math.sinh(j * eta)
  })
  const sinhEta = Math.sinh(etaPrime)
  const tauPrime = Math.sin(xiPrime) / Math.sqrt(sinhEta * sinhEta + Math.cos(xiPrime) ** 2)
  const lambda = Math.atan2(sinhEta, Math.cos(xiPrime))
  return {
    lat: toDegrees(Math.atan(geodeticTangent(tauPrime))),
    lon: normaliseLongitude(toDegrees(lambda) + centralMeridian(zone)),
  }
}
